import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import type {
  ConversationFilterCounts,
  ConversationStatusFilter,
} from "@/lib/conversation-filters";
import { isUnassignedIntegrationFilter } from "@/lib/conversation-integration-assign";

export interface ConversationInboxCountsResult {
  listTotal: number;
  filterCounts: Pick<
    ConversationFilterCounts,
    "mine" | "attention" | "ia" | "all" | "open" | "closed"
  > & { unread?: number };
}

export async function fetchConversationInboxCounts(
  supabase: SupabaseClient<Database>,
  params: {
    workspaceId: string;
    channel: "whatsapp" | "instagram" | "messenger";
    integrationId?: string | null;
    statusFilter: ConversationStatusFilter;
    memberId?: string | null;
  },
): Promise<ConversationInboxCountsResult> {
  const filterUnassigned = isUnassignedIntegrationFilter(params.integrationId);
  const integrationUuid =
    params.integrationId && !filterUnassigned ? params.integrationId : null;

  const { data, error } = await supabase.rpc("get_conversation_inbox_filter_counts", {
    p_workspace_id: params.workspaceId,
    p_channel: params.channel,
    p_integration_id: integrationUuid,
    p_filter_unassigned: filterUnassigned,
    p_status_filter: params.statusFilter,
    p_member_id: params.memberId ?? null,
  });

  if (error) throw error;

  const row = (Array.isArray(data) ? data[0] : null) as Record<string, number | null> | null;
  if (!row) {
    return {
      listTotal: 0,
      filterCounts: {
        mine: 0,
        attention: 0,
        ia: 0,
        all: 0,
        open: 0,
        closed: 0,
      },
    };
  }

  return {
    listTotal: Number(row.list_total ?? 0),
    filterCounts: {
      mine: Number(row.mine ?? 0),
      attention: Number(row.attention ?? 0),
      ia: Number(row.ia ?? 0),
      all: Number(row.all_count ?? 0),
      open: Number(row.open_count ?? 0),
      closed: Number(row.closed_count ?? 0),
      ...(row.unread == null ? {} : { unread: Number(row.unread) }),
    },
  };
}
